import { gql } from "@apollo/client";

/**
 * Resolve the single biometric MFA provider (id + availability). When a
 * deviceId is supplied, `deviceEnrolled` reports whether that device still has
 * a usable server-side key, so the client can reconcile a stale local flag.
 */
export const GetBiometricProviderQuery = gql`
  query GetBiometricProvider($deviceId: String) {
    getBiometricProvider(deviceId: $deviceId) {
      id
      name
      available
      deviceEnrolled
    }
  }
`;

/** Register a device's hardware-backed public key against a biometric provider. */
export const RegisterBiometricDeviceMutation = gql`
  mutation RegisterBiometricDevice($input: RegisterBiometricDeviceInput!) {
    registerBiometricDevice(input: $input) {
      success
      message
    }
  }
`;

/** Request a single-use challenge to be signed by the device. */
export const GetBiometricChallengeMutation = gql`
  mutation GetBiometricChallenge($input: BiometricChallengeInput!) {
    getBiometricChallenge(input: $input) {
      challenge
      expiresAt
      message
    }
  }
`;

/**
 * Verify a signed biometric challenge; returns a verified access token plus the
 * full `loginResult` payload so a returning-user launch bypass (no prior
 * password login) can hydrate all local auth state via persistAllValues.
 */
export const VerifyBiometricAssertionMutation = gql`
  mutation VerifyBiometricAssertion($input: VerifyBiometricInput!) {
    verifyBiometricAssertion(input: $input) {
      accessToken
      verified
      message
      loginResult {
        accessToken
        username
        userId
        expired
        providers
        allowAllPatients
        showDeletedResults
        defaultWardCode
        sharedaccount
        hospid
        hospitalReport
        hospitalReportList
        lastlogin
        state
        resultNotification
        notificationEmail
        email
        clinicalTrial
        name
        mobileNumber
        repeatAtomicComment
        hidePanel
        autoSearch
        discoverable
        wards {
          id
          hospid
          code
          name
        }
      }
    }
  }
`;

/** Disable biometric enrolment for the current user (optionally one device). */
export const DisableBiometricMutation = gql`
  mutation DisableBiometric($input: DisableBiometricInput!) {
    disableBiometric(input: $input) {
      success
      message
    }
  }
`;

/**
 * Unauthenticated cold-launch challenge (returning-user biometric login). No
 * session/token required — the user is resolved server-side from the device.
 */
export const GetBiometricLoginChallengeMutation = gql`
  mutation GetBiometricLoginChallenge($input: BiometricLoginChallengeInput!) {
    getBiometricLoginChallenge(input: $input) {
      challenge
      expiresAt
      message
    }
  }
`;

/**
 * Unauthenticated cold-launch verify: on success returns a fully-verified
 * access token plus the full `loginResult` payload so the app can hydrate all
 * local auth state (bypassing password + MFA).
 */
export const VerifyBiometricLoginMutation = gql`
  mutation VerifyBiometricLogin($input: BiometricLoginInput!) {
    verifyBiometricLogin(input: $input) {
      accessToken
      verified
      message
      loginResult {
        accessToken
        username
        userId
        expired
        providers
        allowAllPatients
        showDeletedResults
        defaultWardCode
        sharedaccount
        hospid
        hospitalReport
        hospitalReportList
        lastlogin
        state
        resultNotification
        notificationEmail
        email
        clinicalTrial
        name
        mobileNumber
        repeatAtomicComment
        hidePanel
        autoSearch
        discoverable
        wards {
          id
          hospid
          code
          name
        }
      }
    }
  }
`;
